import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Enums from '../Enums';

export interface IFeeEntityAttributes extends IModelAttributes {
	amount: number;
	noInterventionAmount: number;
	feeType: Enums.feeType;

	alterationId?: string;
	alteration?: Models.AlterationEntity | Models.IAlterationEntityAttributes;
	feeOptionId?: string;
	feeOption?: Models.FeeOptionEntity | Models.IFeeOptionEntityAttributes;
	transactionId?: string;
	transaction?: Models.TransactionEntity | Models.ITransactionEntityAttributes;
}
@entity('FeeEntity', 'Fee')
export default class FeeEntity extends Model implements IFeeEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<FeeEntity, number>()
	public amount: number;

	@observable
	@attribute<FeeEntity, number>()
	public noInterventionAmount: number;

	@observable
	@attribute<FeeEntity, Enums.feeType>()
	public feeType: Enums.feeType;

	@observable
	@attribute()
	public alterationId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public alteration: Models.AlterationEntity;

	@observable
	@attribute()
	public feeOptionId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public feeOption: Models.FeeOptionEntity;

	@observable
	@attribute()
	public transactionId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public transaction: Models.TransactionEntity;

	constructor(attributes?: Partial<IFeeEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IFeeEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.amount !== undefined) {
				this.amount = attributes.amount;
			}
			if (attributes.noInterventionAmount !== undefined) {
				this.noInterventionAmount = attributes.noInterventionAmount;
			}
			if (attributes.feeType !== undefined) {
				this.feeType = attributes.feeType;
			}
			if (attributes.alterationId !== undefined) {
				this.alterationId = attributes.alterationId;
			}
			if (attributes.alteration !== undefined) {
				if (attributes.alteration === null) {
					this.alteration = attributes.alteration;
				} else if (attributes.alteration instanceof Models.AlterationEntity) {
					this.alteration = attributes.alteration;
					this.alterationId = attributes.alteration.id;
				} else {
					this.alteration = new Models.AlterationEntity(attributes.alteration);
					this.alterationId = this.alteration.id;
				}
			}
			if (attributes.feeOptionId !== undefined) {
				this.feeOptionId = attributes.feeOptionId;
			}
			if (attributes.feeOption !== undefined) {
				if (attributes.feeOption === null) {
					this.feeOption = attributes.feeOption;
				} else if (attributes.feeOption instanceof Models.FeeOptionEntity) {
					this.feeOption = attributes.feeOption;
					this.feeOptionId = attributes.feeOption.id;
				} else {
					this.feeOption = new Models.FeeOptionEntity(attributes.feeOption);
					this.feeOptionId = this.feeOption.id;
				}
			}
			if (attributes.transactionId !== undefined) {
				this.transactionId = attributes.transactionId;
			}
			if (attributes.transaction !== undefined) {
				if (attributes.transaction === null) {
					this.transaction = attributes.transaction;
				} else if (attributes.transaction instanceof Models.TransactionEntity) {
					this.transaction = attributes.transaction;
					this.transactionId = attributes.transaction.id;
				} else {
					this.transaction = new Models.TransactionEntity(attributes.transaction);
					this.transactionId = this.transaction.id;
				}
			}
		}
	}
}
